import React, { Component, ReactElement } from 'react';

import _get from 'lodash.get';

import { ColumnType } from '@philips-emr/tws-components-api';
import { TextAlignment } from '@philips-emr/tws-constants';
import { RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { format } from '@philips-emr/tws-core/lib/services/model-service';
import { classNames, isFunction } from '@philips-emr/tws-utils';

import { CollapseArrow, SuperscriptionItem, Tooltip } from '../..';
import { TooltipTrigger } from '../../tooltip/constants';
import { ColumnGroupsProps } from '../data-table';

import { DataTableColumnProps, Superscription } from './data-table-column';

import { DataTableCell, DataTableCheckbox } from '.';

function getFlexAlignment(alignment?: TextAlignment): string {
  const options = {
    [TextAlignment.CENTER]: 'u-flex-jc-center',
    [TextAlignment.INITIAL]: '',
    [TextAlignment.JUSTIFY]: '',
    [TextAlignment.LEFT]: 'u-flex-jc-start',
    [TextAlignment.RIGHT]: 'u-flex-jc-end',
  };

  if (alignment && alignment in options) {
    return options[alignment];
  }

  return options[TextAlignment.CENTER];
}

type DynamicType = 'column' | 'row';

interface CellOptions {
  column: Partial<DataTableColumnProps>;
  index: number;
  value?: any;
}

interface DataTableRowProps {
  checked: boolean;
  collapsed: boolean;
  columnGroups: ColumnGroupsProps;
  data: Record<string, any>;
  index: number;
  key: string;
  multiselect: boolean;
  onChange: (data: Record<string, any>) => void;
  onClick: (data: Record<string, any>) => void;
  onDynamicContentRender: (event: RenderEvent) => void;
  width: number;
  withExpandedIndicator: boolean;
}

interface DataTableRowState {
  hovering: boolean;
}

class DataTableRow extends Component<DataTableRowProps, DataTableRowState> {
  state: DataTableRowState = {
    hovering: false,
  };

  static defaultProps = {
    collapsed: false,
    columns: [],
    data: {},
  };

  onRowClick = (): void => {
    const { data, onClick } = this.props;

    isFunction(onClick) && onClick(data);
  };

  onMouseEnter = (): void => {
    this.setState({ hovering: true });
  };

  onMouseLeave = (): void => {
    this.setState({ hovering: false });
  };

  getSuperscription(value: number | string, superscription: Superscription[] = []): Superscription | void {
    return superscription.find((item) => item.value === String(value));
  }

  getDynamicContent(entry: string = '', type: DynamicType, value: any): ReactElement {
    const { data, onDynamicContentRender } = this.props;

    let content = value;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const defaultFunction = (_: object): ReactElement | null => null;
    let renderFn = defaultFunction;

    const render = (fn: (data: object) => ReactElement): void => {
      renderFn = fn;
    };

    onDynamicContentRender(new RenderEvent({ data, entry, render, type }));

    if (renderFn !== defaultFunction && isFunction(renderFn)) {
      content = renderFn(data);
    }

    return content;
  }

  getCheckboxCell = (options: CellOptions): ReactElement => {
    const { column, index, value } = options;

    return (
      <DataTableCell
        key={`data-table-cell-${column.entry}-${index}`}
        {...column}
      >
        <DataTableCheckbox name={column.entry} value={value} />
      </DataTableCell>
    );
  };

  getChevronCell = (options: CellOptions): ReactElement => {
    const { column, index, value } = options;
    const { onClick, collapsed } = this.props;
    const content = this.getDynamicContent(column.entry, 'column', value);

    return (
      <DataTableCell
        key={`data-table-cell-${column.entry}-${index}`}
        {...column}
      >
        { content && <CollapseArrow collapsed={collapsed} onClick={onClick} /> }
      </DataTableCell>
    );
  };

  getDefaultCell = (options: CellOptions): ReactElement => {
    const { column, index, value } = options;
    const { formatters = {} } = column;

    const cellFormatter = formatters['value'];
    const formattedValue = cellFormatter ? format(cellFormatter.name, value, cellFormatter.options) : value;

    return (
      <DataTableCell
        key={`data-table-cell-${column.entry}-${index}`}
        {...column}
      >
        <Tooltip trigger={TooltipTrigger.ELLIPSIS} value={formattedValue}>
          <div className="u-truncate">{formattedValue}</div>
        </Tooltip>
      </DataTableCell>
    );
  };

  getDynamicCell = (options: CellOptions): ReactElement => {
    const { column, index, value } = options;

    const content = this.getDynamicContent(column.entry, 'column', value);

    return (
      <DataTableCell
        key={`data-table-cell-${column.entry}-${index}`}
        {...column}
      >
        {content}
      </DataTableCell>
    );
  };

  getSuperscriptionCell = (options: CellOptions): ReactElement => {
    const { column, index, value } = options;
    const { showSuperscriptionLabel, superscription } = column;

    const items = Array.isArray(value) ? value : [].concat(value);

    const className = `${getFlexAlignment(column.alignment)} u-truncate`;

    return (
      <DataTableCell
        key={`data-table-cell-${column.entry}-${index}`}
        {...column}
      >
        {items.map((_, index) => (
          <SuperscriptionItem
            className={className}
            key={index}
            showLabel={showSuperscriptionLabel}
            {...this.getSuperscription(value, superscription) || {}}
          />
        ))}
      </DataTableCell>
    );
  };

  getSuperscriptionBarCell = (options: CellOptions): ReactElement => {
    const { column, index, value } = options;
    const { showSuperscriptionLabel, superscription } = column;

    const { color, label } =
      this.getSuperscription(value, superscription) || {};

    const style = {
      backgroundColor: color,
    };

    if (!showSuperscriptionLabel) {
      return (
        <Tooltip
          key={`data-table-cell-${column.entry}-${index}`}
          value={label}
          placement={TextAlignment.RIGHT}
        >
          <div className="c-data-table__cell" style={style} />
        </Tooltip>
      );
    }

    return (
      <DataTableCell
        key={`data-table-cell-${column.entry}-${index}`}
        {...column}
        className="u-no-padding"
      >
        <div className="c-data-table__superscription-bar-wrapper">
          <Tooltip value={label}>
            <div
              className="c-data-table__superscription-bar-block"
              style={style}
            />
          </Tooltip>
          <div className="c-data-table__superscription-bar-label u-truncate">
            {label}
          </div>
        </div>
      </DataTableCell>
    );
  };

  renderCell(options: CellOptions): ReactElement {
    const { data } = this.props;
    const { entry = '', type = ColumnType.DEFAULT } = options.column;

    const value = _get(data, entry);

    const cells = {
      [ColumnType.CHECKBOX]: this.getCheckboxCell,
      [ColumnType.CHEVRON]: this.getChevronCell,
      [ColumnType.DEFAULT]: this.getDefaultCell,
      [ColumnType.DYNAMIC]: this.getDynamicCell,
      [ColumnType.SUPERSCRIPTION]: this.getSuperscriptionCell,
      [ColumnType.SUPERSCRIPTION_BAR]: this.getSuperscriptionBarCell,
    };

    const fn = cells[type];

    return fn({ ...options, value });
  }

  renderMultiSelectColumnCell(): ReactElement | null {
    const { checked, data, index, multiselect, onChange } = this.props;

    if (!multiselect) {
      return null;
    }

    return (
      <DataTableCell width={72}>
        <DataTableCheckbox
          name={`data-table-row-multiselect-${index}`}
          value={checked}
          onChange={() => onChange(data)}
        />
      </DataTableCell>
    );
  }

  getClassName(): string {
    const { index, checked } = this.props;
    const { hovering } = this.state;

    return classNames({
      'c-data-table__row': true,
      'is-checked': checked,
      'is-even': index % 2 !== 0,
      'is-hovering': hovering,
    });
  }

  renderCells(): ReactElement {
    const { columnGroups } = this.props;

    // barGroups must come before anything else.
    const { barColumns = [], defaultColumns = [] } = columnGroups;

    return (
      <div className={this.getClassName()} data-testid="data-table-row" onClick={this.onRowClick}>
        {barColumns.map((column, index) =>
          this.renderCell({ column, index })
        )}

        {this.renderMultiSelectColumnCell()}

        {defaultColumns.map((column, index) =>
          this.renderCell({ column, index })
        )}
      </div>
    );
  }

  renderCollapsedContent(): ReactElement | null {
    const { collapsed, width, withExpandedIndicator } = this.props;

    if (collapsed) {
      return null;
    }

    const content = this.getDynamicContent('', 'row', null);

    if (!content) {
      return null;
    }

    const style = {
      width: `${width}px`,
    };

    const contentClassName = classNames({
      'c-data-table__row-expanded-content': true,
      'u-hide@empty': true,
      'with-expanded-indicator': withExpandedIndicator,
    });

    return (
      <div className="c-data-table__row-expanded u-hide@empty" data-testid="data-table-row-expanded" onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
        <div className={contentClassName} style={style}>
          {content}
        </div>
      </div>
    );
  }

  render(): ReactElement {
    return (
      <>
        {this.renderCells()}
        {this.renderCollapsedContent()}
      </>
    );
  }
}

export default DataTableRow;
